import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { AppState } from '.';
import {
  exceptionOf,
  PaymentMethod,
  PaymentMethodType,
  SerializedException,
  StripePaymentConfig,
} from '../models';
import { Operator } from '../models/query';
import { PaymentMethodService } from '../services';

export const PAYMENT_METHOD_FEATURE_KEY = 'paymentMethod';

interface PaymentMethodState {
  selectedPaymentType: PaymentMethodType;
  userSelectedPaymentType: boolean;
  defaultPaymentMethod?: PaymentMethodType;
  cardInformationInvalid: boolean,
  isCardEditing: boolean;
  stripePaymentConfig: StripePaymentConfig;
  loading: boolean;
  error?: string;
  /** Counts create/update/delete results, so a card list that predates one cannot drop its card. */
  paymentMutations: number;
  /** The newest card-list request and the mutation count when it started. */
  cardListRequest?: { requestId: string; mutationsAtStart: number };
}

const paymentMethodAdapter = createEntityAdapter<PaymentMethod>();

export const createInitialState = (partialState: Partial<PaymentMethodState> = {}) => (
  paymentMethodAdapter.getInitialState({
    selectedPaymentType: PaymentMethodType.CREDIT_CARD,
    userSelectedPaymentType: false,
    cardInformationInvalid: true,
    isCardEditing: false,
    stripePaymentConfig: undefined,
    loading: false,
    error: undefined,
    paymentMutations: 0,
    cardListRequest: undefined,
    ...partialState,
  })
);

export const doCreatePaymentMethod = createAsyncThunk(
  'paymentMethod/createPaymentMethod',
  async (input: Parameters<typeof PaymentMethodService['createPaymentMethod']>[0], { rejectWithValue }) => {
    try {
      const paymentMethod = await PaymentMethodService.createPaymentMethod(input);
      return paymentMethod;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doGetPaymentMethod = createAsyncThunk(
  'paymentMethod/getPaymentMethod',
  async (accountId: PaymentMethod['accountId'], { rejectWithValue }) => {
    try {
      const paymentMethod = await PaymentMethodService.getPaymentMethods(
        { field: 'accountId', operator: Operator.EQ, value: accountId.toString() },
        { field: 'archived', operator: Operator.EQ, value: false },
      );

      return paymentMethod;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doGetDefaultPaymentMethod = createAsyncThunk(
  'paymentMethod/getDefaultPaymentMethod',
  async (_, { rejectWithValue }) => {
    try {
      return await PaymentMethodService.getDefaultPaymentMethod();
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doUpdatePaymentMethod = createAsyncThunk(
  'paymentMethod/updatePaymentMethod',
  async (input: Parameters<typeof PaymentMethodService['updatePaymentMethod']>[0], { rejectWithValue }) => {
    try {
      return await PaymentMethodService.updatePaymentMethod(input);
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doUpdateDefaultPaymentMethod = createAsyncThunk(
  'paymentMethod/updateDefaultPaymentMethod',
  async (input: Parameters<typeof PaymentMethodService['updateDefaultPaymentMethod']>[0], { rejectWithValue }) => {
    try {
      return await PaymentMethodService.updateDefaultPaymentMethod(input);
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doDeletePaymentMethod = createAsyncThunk(
  'paymentMethod/deletePaymentMethod',
  async (input: Parameters<typeof PaymentMethodService['deletePaymentMethod']>[0], { rejectWithValue }) => {
    try {
      return await PaymentMethodService.deletePaymentMethod(input);
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doGetClientSecret = createAsyncThunk(
  'paymentMethod/getClientSecret',
  async (_, { rejectWithValue }) => {
    try {
      return await PaymentMethodService.getClientSecret();
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const testOpsPaymentMethodSlice = createSlice({
  name: PAYMENT_METHOD_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    doChangeSelectedPaymentMethod(state, action: PayloadAction<PaymentMethodType>) {
      state.selectedPaymentType = action.payload;
      state.userSelectedPaymentType = true;
    },
    doSetSelectedPaymentMethod(state, action: PayloadAction<PaymentMethodType>) {
      state.selectedPaymentType = action.payload;
      state.userSelectedPaymentType = false;
    },
    doUpdateCardInformationInvalid(state, action: PayloadAction<boolean>) {
      state.cardInformationInvalid = action.payload;
    },
    doEnableCardEditing(state, action: PayloadAction<boolean>) {
      state.isCardEditing = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(doCreatePaymentMethod.pending, state => {
      state.loading = true;
    });
    builder.addCase(doCreatePaymentMethod.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentMutations += 1;
      paymentMethodAdapter.addOne(state, action.payload);
      state.cardInformationInvalid = false;
      state.error = undefined;
    });
    builder.addCase(doCreatePaymentMethod.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
    builder.addCase(doGetPaymentMethod.pending, (state, action) => {
      state.loading = true;
      state.cardListRequest = {
        requestId: action.meta.requestId,
        mutationsAtStart: state.paymentMutations,
      };
    });
    builder.addCase(doGetPaymentMethod.fulfilled, (state, action) => {
      state.loading = false;
      // Only the newest list request that no create/update/delete overtook describes the account's
      // cards. Any other response is stale (it can still contain a card deleted since, or an
      // outdated copy of an updated one), so it is dropped whole instead of being merged.
      const request = state.cardListRequest;
      const isCurrent = request?.requestId === action.meta.requestId
        && request.mutationsAtStart === state.paymentMutations;
      if (!isCurrent) return;

      // An empty list is a valid answer ("no saved card"), and the list is the account's full set
      // of cards, so cards of that account missing from it are gone (deleted elsewhere).
      const returnedIds = new Set(action.payload.map(it => it.id));
      const obsoleteIds = paymentMethodAdapter.getSelectors().selectAll(state)
        .filter(it => it.accountId === action.meta.arg && !returnedIds.has(it.id))
        .map(it => it.id);
      paymentMethodAdapter.removeMany(state, obsoleteIds);
      paymentMethodAdapter.upsertMany(state, action.payload);
      if (action.payload.length > 0) {
        state.cardInformationInvalid = false;
      } else if (obsoleteIds.length > 0) {
        state.cardInformationInvalid = true;
      }
      state.error = undefined;
    });
    builder.addCase(doGetPaymentMethod.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
    builder.addCase(doGetDefaultPaymentMethod.pending, state => {
      state.loading = true;
    });
    builder.addCase(doGetDefaultPaymentMethod.fulfilled, (state, action) => {
      state.loading = false;
      state.defaultPaymentMethod = action.payload;
      if (!state.userSelectedPaymentType) {
        state.selectedPaymentType = action.payload;
      }
      state.error = undefined;
    });
    builder.addCase(doGetDefaultPaymentMethod.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
    builder.addCase(doUpdatePaymentMethod.pending, state => {
      state.loading = true;
    });
    builder.addCase(doUpdatePaymentMethod.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentMutations += 1;
      paymentMethodAdapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      });
      state.cardInformationInvalid = false;
      state.error = undefined;
    });
    builder.addCase(doUpdatePaymentMethod.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
    builder.addCase(doUpdateDefaultPaymentMethod.pending, state => {
      state.loading = true;
    });
    builder.addCase(doUpdateDefaultPaymentMethod.fulfilled, (state, action) => {
      state.loading = false;
      state.defaultPaymentMethod = action.payload;
      state.error = undefined;
    });
    builder.addCase(doUpdateDefaultPaymentMethod.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
    builder.addCase(doDeletePaymentMethod.pending, state => {
      state.loading = true;
    });
    builder.addCase(doDeletePaymentMethod.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentMutations += 1;
      paymentMethodAdapter.removeOne(state, action.payload.id);
      state.cardInformationInvalid = true;
      state.error = undefined;
    });
    builder.addCase(doDeletePaymentMethod.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
    builder.addCase(doGetClientSecret.pending, state => {
      state.loading = true;
    });
    builder.addCase(doGetClientSecret.fulfilled, (state, action) => {
      state.loading = false;
      state.stripePaymentConfig = action.payload;
      state.error = undefined;
    });
    builder.addCase(doGetClientSecret.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as SerializedException)?.message;
    });
  },
});

const selectPaymentMethodFeature = (state: AppState) => state[PAYMENT_METHOD_FEATURE_KEY];
const { selectAll } = paymentMethodAdapter.getSelectors(selectPaymentMethodFeature);
export const selectOneByAccountId = (accountId: PaymentMethod['accountId']) => createSelector(
  selectAll,
  paymentMethods => paymentMethods.find(it => it.accountId === accountId),
);
export const selectLoading = createSelector(
  selectPaymentMethodFeature,
  paymentMethodState => paymentMethodState.loading,
);

export const selectSelectedPaymentMethod = createSelector(
  selectPaymentMethodFeature,
  it => it.selectedPaymentType,
);

export const selectDefaultPaymentMethod = createSelector(
  selectPaymentMethodFeature,
  it => it.defaultPaymentMethod,
);

export const isCardInformationInvalid = createSelector(
  selectPaymentMethodFeature,
  it => it.cardInformationInvalid,
);

export const isCardEditing = createSelector(
  selectPaymentMethodFeature,
  it => it.isCardEditing,
);

export const selectStripePaymentConfig = createSelector(
  selectPaymentMethodFeature,
  it => it.stripePaymentConfig,
);

export const {
  doChangeSelectedPaymentMethod,
  doSetSelectedPaymentMethod,
  doUpdateCardInformationInvalid,
  doEnableCardEditing,
} = testOpsPaymentMethodSlice.actions;

export default testOpsPaymentMethodSlice.reducer;
